import useFetch from 'hooks/useFetch';
import qs from 'qs';
import { mergeLeft } from 'ramda';
import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { selectAuthenticityToken } from 'state/authenticityToken';
import { selectTenant } from 'state/tenant';
import { selectCorrelationId } from 'state/correlationId';
import {
  selectSessionToken,
  loadSessionTokenFromLocalStore,
  saveSessionTokenToLocalStore,
  setSessionToken,
} from 'state/sessionToken';
import { Status } from './requests/fetchUtils';
import parse from 'url-parse';
import env from 'utils/env';
/**
 * Builder
 */
const getOptions = (
  options: RequestInit | undefined,
  authenticityToken: string,
  sessionToken: string,
  clientName: string,
  correlationId: string,
) => {
  const defaultOptions: RequestInit = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': authenticityToken,
      'X-Client-Name': clientName,
      Authorization: 'Bearer ' + sessionToken,
      'Correlation-Id': correlationId,
    },
  };
  return mergeLeft(options, defaultOptions);
};

type ApiRequestInit = Omit<RequestInit, 'body'> & {
  body?: object;
};

export type ApiFetch = (
  input: string,
  init: ApiRequestInit,
) => Promise<Response>;

const useFetchApi = (): [Status, ApiFetch] => {
  const [status, fetch] = useFetch();
  const dispatch = useDispatch();
  const store = useStore();

  const setSessionTokenFromResponse = useCallback(
    (response: Response) => {
      const sessionToken = response.headers.get('X-Session-Token');
      if (sessionToken) {
        saveSessionTokenToLocalStore(sessionToken);
        dispatch(setSessionToken(sessionToken));
      }
    },
    [dispatch],
  );
  const newFetch: ApiFetch = useCallback(
    async (input, options) => {
      input = env.BASENAME + input;

      const state = store.getState();
      const { overrideConfig, clientName } = selectTenant(state);
      const correlationId = selectCorrelationId(state);

      const authenticityToken = selectAuthenticityToken(state);

      let { token } = selectSessionToken(state);
      const sessionTokenFromLocalStore = loadSessionTokenFromLocalStore();
      if (sessionTokenFromLocalStore) token = sessionTokenFromLocalStore;

      let body = JSON.stringify(options.body);

      if (options.method === undefined || options.method === 'GET') {
        const parsed = parse(input);
        const oldQueryString = ((parsed.query as unknown) as string).replace(
          /^\?/,
          '',
        );
        const oldQuery = qs.parse(oldQueryString);
        const newQuery = { overrideConfig, ...oldQuery };
        parsed.set('query', qs.stringify(newQuery));
        input = parsed.toString();
      } else {
        body = JSON.stringify({ ...options.body, overrideConfig });
      }

      const mergeOptions = {
        ...options,
        body,
      };

      const newOptions = getOptions(
        mergeOptions,
        authenticityToken,
        token,
        clientName,
        correlationId,
      );
      try {
        const response = await fetch(input, newOptions);
        setSessionTokenFromResponse(response);
        return response;
      } catch (e) {
        if (e.response) {
          setSessionTokenFromResponse(e.response);
        }
        throw e;
      }
    },
    [store, fetch, setSessionTokenFromResponse],
  );

  return [status, newFetch];
};

export default useFetchApi;
